import { useEffect, useState, useMemo } from "react";
import { api, formatError } from "@/lib/api";
import { Card } from "../Dashboard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Search, UserCog, Sparkles, Wrench, ShieldAlert, Trash2, RotateCcw, ClipboardCopy } from "lucide-react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import BillingPanel from "@/components/AdminBillingPanel";

const STATUSES = ["prospect", "trial", "active", "expired", "suspended", "cancelled"];
const MEMBERSHIPS = ["founding_customer", "trial", "starter", "growth", "business", "enterprise"];
// Billing filter chips - map to either `membership=` (founding) or `billing_status=`.
const BILLING_FILTERS = [
  { key: "all",              label: "All",            kind: null },
  { key: "founding_customer",label: "Founding",       kind: "membership"      },
  { key: "trial",            label: "Trial",          kind: "membership"      },
  { key: "active",           label: "Active",         kind: "billing_status"  },
  { key: "payment_issue",    label: "Payment issue",  kind: "billing_status"  },
  { key: "suspended",        label: "Suspended",      kind: "status"          },
  { key: "cancelled",        label: "Cancelled",      kind: "billing_status"  },
  { key: "expired",          label: "Expired",        kind: "billing_status"  },
];

const STATUS_PILL = {
  prospect:  "bg-slate-100 text-slate-700",
  trial:     "bg-amber-100 text-amber-800",
  active:    "bg-emerald-100 text-emerald-800",
  expired:   "bg-orange-100 text-orange-800",
  suspended: "bg-red-100 text-red-700",
  cancelled: "bg-slate-200 text-slate-500",
};

/** Super Admin CRM - companies. List + filters + drawer-style edit. */
export default function AdminCompaniesPage() {
  const nav = useNavigate();
  const [rows, setRows] = useState([]);
  const [stats, setStats] = useState({ by_status: {} });
  const [filters, setFilters] = useState({ q: "", status: "all", membership: "all", active_within_days: "all", billing_filter: "all" });
  const [editing, setEditing] = useState(null);
  const [testTarget, setTestTarget] = useState(null);

  const load = async () => {
    const params = {};
    if (filters.q) params.q = filters.q;
    if (filters.status !== "all") params.status = filters.status;
    if (filters.membership !== "all") params.membership = filters.membership;
    if (filters.active_within_days !== "all") params.active_within_days = filters.active_within_days;
    // Billing chip overlays one of the other params depending on what it maps to.
    const bf = BILLING_FILTERS.find(b => b.key === filters.billing_filter);
    if (bf && bf.kind === "membership")     params.membership     = bf.key;
    if (bf && bf.kind === "billing_status") params.billing_status = bf.key;
    if (bf && bf.kind === "status")         params.status         = bf.key;
    const [{ data: cs }, { data: st }] = await Promise.all([
      api.get("/admin/companies", { params }),
      api.get("/stats/platform").catch(() => ({ data: { by_status: {} } })),
    ]);
    setRows(cs); setStats(st);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filters]);

  const impersonate = async (companyId) => {
    // Find a company_admin user for this company and start impersonation
    try {
      const { data: users } = await api.get("/admin/users", { params: { company_id: companyId, role: "company_admin" } });
      if (!users?.length) { toast.error("No Workspace Admin exists for this company."); return; }
      const target = users[0];
      if (!window.confirm(`Impersonate ${target.name} (${target.email})? This will be audit-logged.`)) return;
      await api.post(`/admin/impersonate/${target.user_id}`);
      toast.success(`Impersonating ${target.name}`);
      nav("/app");
      window.location.reload();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  const statusCards = useMemo(() => STATUSES.map(s => ({ s, n: stats.by_status?.[s] || 0 })), [stats]);

  return (
    <div>
      <div className="flex items-end justify-between mb-6 flex-wrap gap-3">
        <div>
          <div className="eyebrow">Super Admin</div>
          <h1 className="font-display text-4xl mt-1">Companies</h1>
          <p className="text-sm text-[#4B5773] mt-2 max-w-xl">
            Trial, customer and prospect accounts on the NextRota platform.
          </p>
        </div>
      </div>

      {/* Status summary cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-6" data-testid="admin-status-cards">
        {statusCards.map(({ s, n }) => (
          <div key={s} className="rounded-lg border border-slate-200 bg-white p-3">
            <div className="text-[10px] uppercase tracking-widest text-[#4B5773]">{s}</div>
            <div className="font-display text-2xl mt-1">{n}</div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <Card className="mb-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <div className="relative md:col-span-2">
            <Search className="absolute left-2 top-3 h-4 w-4 text-slate-400"/>
            <Input value={filters.q} placeholder="Search company, contact name, email"
              data-testid="admin-companies-search"
              onChange={(e)=>setFilters({...filters, q: e.target.value})}
              className="h-10 pl-8 rounded-md"/>
          </div>
          <Select value={filters.status} onValueChange={(v)=>setFilters({...filters, status: v})}>
            <SelectTrigger data-testid="admin-status-filter" className="h-10 rounded-md"><SelectValue placeholder="Status"/></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              {STATUSES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filters.membership} onValueChange={(v)=>setFilters({...filters, membership: v})}>
            <SelectTrigger data-testid="admin-membership-filter" className="h-10 rounded-md"><SelectValue placeholder="Membership"/></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All memberships</SelectItem>
              {MEMBERSHIPS.map(m => <SelectItem key={m} value={m}>{m.replace("_", " ")}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        {/* Billing filter chips for quick CRM segmentation */}
        <div className="mt-4 flex flex-wrap gap-2" data-testid="admin-billing-chips">
          {BILLING_FILTERS.map(bf => (
            <button
              key={bf.key}
              data-testid={`admin-billing-chip-${bf.key}`}
              onClick={()=>setFilters({...filters, billing_filter: bf.key})}
              className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors ${
                filters.billing_filter === bf.key
                  ? "bg-[#0B1A36] text-white border-[#0B1A36]"
                  : "bg-white text-[#0B1A36] border-slate-200 hover:bg-slate-50"
              }`}>
              {bf.label}
            </button>
          ))}
        </div>
      </Card>

      {/* Table */}
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-[#EEF3FB]/95 text-[10px] uppercase tracking-widest text-[#4B5773]">
              <tr>
                <th className="text-left px-4 py-3">Company</th>
                <th className="text-left px-3 py-3">Status</th>
                <th className="text-left px-3 py-3">Membership</th>
                <th className="text-left px-3 py-3">Billing</th>
                <th className="text-left px-3 py-3">Trial end</th>
                <th className="text-left px-3 py-3">Employees</th>
                <th className="text-left px-3 py-3">Last activity</th>
                <th className="text-right px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {rows.map(c => (
                <tr key={c.company_id} className="hover:bg-[#F6F9FE]" data-testid={`admin-co-row-${c.company_id}`}>
                  <td className="px-4 py-3">
                    <button onClick={()=>setEditing(c)} className="text-left">
                      <div className="font-medium text-[#0B1A36] flex items-center gap-1.5">
                        {c.name}
                        {c.is_test && (
                          <span className="inline-flex items-center gap-0.5 text-[10px] uppercase tracking-widest font-semibold px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-800"
                                data-testid={`admin-row-is-test-${c.company_id}`}>
                            Test
                          </span>
                        )}
                        {c.membership_type === "founding_customer" && c.founding_member_number && (
                          <span className="inline-flex items-center gap-0.5 text-[10px] uppercase tracking-widest font-semibold px-1.5 py-0.5 rounded-full bg-[#0B1A36] text-white"
                                data-testid={`admin-row-founding-${c.company_id}`}>
                            <Sparkles className="h-2.5 w-2.5"/>#{c.founding_member_number}
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-[#4B5773]">{c.contact_name || c.contact_email || c.country}</div>
                    </button>
                  </td>
                  <td className="px-3"><span className={`text-[10px] uppercase tracking-widest font-semibold px-2 py-0.5 rounded-full ${STATUS_PILL[c.status]||"bg-slate-100 text-slate-700"}`}>{c.status || "-"}</span></td>
                  <td className="px-3 text-[#4B5773]">{(c.membership_type||"-").replace("_"," ")}</td>
                  <td className="px-3">
                    {c.billing_status ? (
                      <span className={`text-[10px] uppercase tracking-widest font-semibold px-2 py-0.5 rounded-full ${
                        c.billing_status === "active"        ? "bg-emerald-100 text-emerald-800" :
                        c.billing_status === "payment_issue" ? "bg-amber-100 text-amber-800" :
                        c.billing_status === "cancelled"     ? "bg-slate-200 text-slate-600" :
                        c.billing_status === "expired"       ? "bg-orange-100 text-orange-800" :
                        "bg-slate-100 text-slate-700"
                      }`} data-testid={`admin-row-billing-${c.company_id}`}>{c.billing_status.replace("_"," ")}</span>
                    ) : <span className="text-[#94A3B8]">-</span>}
                    {c.access_state?.access === "restricted" && (
                      <span className="ml-1 text-[10px] uppercase tracking-widest font-semibold px-1.5 py-0.5 rounded-full bg-red-100 text-red-700"
                            data-testid={`admin-row-restricted-${c.company_id}`}>Restricted</span>
                    )}
                  </td>
                  <td className="px-3 text-[#4B5773]">{c.trial_end_date || "-"}</td>
                  <td className="px-3 text-[#4B5773]">{c.employee_count}</td>
                  <td className="px-3 text-[#4B5773] text-xs">{c.last_activity_at?.slice(0,10) || "-"}</td>
                  <td className="px-4 text-right">
                    <Button size="sm" variant="ghost" onClick={()=>impersonate(c.company_id)}
                      data-testid={`admin-impersonate-${c.company_id}`}
                      className="text-[#1D4ED8] hover:bg-[#DBE7FE]">
                      <UserCog className="h-4 w-4 mr-1"/>Impersonate
                    </Button>
                    <Button size="sm" variant="ghost" onClick={()=>setTestTarget(c)}
                      data-testid={`admin-test-tools-${c.company_id}`}
                      className="text-[#4B5773] hover:bg-slate-100"
                      title="Test tools (Super Admin cleanup)">
                      <Wrench className="h-4 w-4 mr-1"/>Test tools
                    </Button>
                  </td>
                </tr>
              ))}
              {!rows.length && (
                <tr><td colSpan={8} className="py-8 text-center text-[#4B5773]">No companies match the filter.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <CompanyEditDialog company={editing} onClose={()=>setEditing(null)} onSaved={load}/>
      <TestToolsDialog company={testTarget} onClose={()=>setTestTarget(null)} onDeleted={load}/>
    </div>
  );
}

function CompanyEditDialog({ company, onClose, onSaved }) {
  const [form, setForm] = useState({});
  const [tab, setTab] = useState("details");
  const [original, setOriginal] = useState({});
  useEffect(() => {
    if (company) {
      setTab("details");
      const seed = {
        status: company.status || "trial",
        membership_type: company.membership_type || "founding_customer",
        trial_start_date: company.trial_start_date || "",
        trial_end_date: company.trial_end_date || "",
        membership_expiry_date: company.membership_expiry_date || "",
        contact_name: company.contact_name || "",
        contact_email: company.contact_email || "",
        notes: company.notes || "",
        is_test: !!company.is_test,
      };
      setForm(seed);
      setOriginal(seed);
    }
  }, [company]);
  if (!company) return null;

  // Phase 2Z.ab - require explicit confirmation for risky CRM edits
  // (trial expiry, status, membership, contractual expiry). Notes /
  // contact-name / contact-email changes save without an extra prompt.
  const RISKY_FIELDS = ["status", "membership_type", "trial_start_date",
                        "trial_end_date", "membership_expiry_date"];
  const riskyDiff = () => RISKY_FIELDS
    .filter(k => (form[k] || "") !== (original[k] || ""))
    .map(k => `${k}: ${original[k] || "(empty)"} -> ${form[k] || "(empty)"}`);

  const save = async (e) => {
    e.preventDefault();
    const changes = riskyDiff();
    if (changes.length) {
      const ok = window.confirm(
        `Confirm changes to ${company.name}:\n\n` +
        changes.join("\n") +
        `\n\nThis is audit-logged.`
      );
      if (!ok) return;
    }
    try {
      await api.patch(`/admin/companies/${company.company_id}`, form);
      toast.success("Saved");
      onSaved(); onClose();
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  };

  return (
    <Dialog open={!!company} onOpenChange={(o)=>!o && onClose()}>
      <DialogContent fullscreenOnMobile className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>{company.name}</DialogTitle></DialogHeader>

        {/* Tab strip */}
        <div className="flex gap-1 border-b border-slate-200 -mx-6 px-6">
          {[["details","CRM details"],["billing","Billing & Stripe"],["activity","Activity"]].map(([k,l]) => (
            <button key={k} onClick={()=>setTab(k)}
              data-testid={`admin-edit-tab-${k}`}
              className={`px-3 py-2 text-sm font-medium border-b-2 transition-colors ${
                tab === k ? "border-[#2563EB] text-[#1D4ED8]" : "border-transparent text-[#4B5773] hover:text-[#0B1A36]"
              }`}>{l}</button>
          ))}
        </div>

        {tab === "details" ? (
          <form onSubmit={save} className="space-y-4 mt-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="eyebrow">Status</Label>
                <Select value={form.status} onValueChange={(v)=>setForm({...form, status: v})}>
                  <SelectTrigger data-testid="admin-edit-status" className="mt-1.5 h-11 rounded-lg"><SelectValue/></SelectTrigger>
                  <SelectContent>{STATUSES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="eyebrow">Membership</Label>
                <Select value={form.membership_type} onValueChange={(v)=>setForm({...form, membership_type: v})}>
                  <SelectTrigger data-testid="admin-edit-membership" className="mt-1.5 h-11 rounded-lg"><SelectValue/></SelectTrigger>
                  <SelectContent>{MEMBERSHIPS.map(m => <SelectItem key={m} value={m}>{m.replace("_"," ")}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="eyebrow">Trial start</Label>
                <Input type="date" value={form.trial_start_date||""} data-testid="admin-edit-trial-start"
                  onChange={(e)=>setForm({...form, trial_start_date: e.target.value})} className="mt-1.5 h-11 rounded-lg"/>
              </div>
              <div>
                <Label className="eyebrow">Trial end</Label>
                <Input type="date" value={form.trial_end_date||""} data-testid="admin-edit-trial-end"
                  onChange={(e)=>setForm({...form, trial_end_date: e.target.value})} className="mt-1.5 h-11 rounded-lg"/>
              </div>
            </div>
            <div>
              <Label className="eyebrow">Membership expiry</Label>
              <Input type="date" value={form.membership_expiry_date||""} data-testid="admin-edit-expiry"
                onChange={(e)=>setForm({...form, membership_expiry_date: e.target.value})} className="mt-1.5 h-11 rounded-lg"/>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="eyebrow">Contact name</Label>
                <Input value={form.contact_name} data-testid="admin-edit-contact-name"
                  onChange={(e)=>setForm({...form, contact_name: e.target.value})} className="mt-1.5 h-11 rounded-lg"/>
              </div>
              <div>
                <Label className="eyebrow">Contact email</Label>
                <Input type="email" value={form.contact_email} data-testid="admin-edit-contact-email"
                  onChange={(e)=>setForm({...form, contact_email: e.target.value})} className="mt-1.5 h-11 rounded-lg"/>
              </div>
            </div>
            <div>
              <Label className="eyebrow">CRM notes</Label>
              <textarea value={form.notes||""} rows={4} data-testid="admin-edit-notes"
                onChange={(e)=>setForm({...form, notes: e.target.value})}
                placeholder="Initial contact, requested features, follow-up date…"
                className="mt-1.5 w-full px-3 py-2 rounded-lg border border-slate-200 text-sm"/>
            </div>
            {/* Feb 2026 email guard V1 -- Super Admin only. Suppresses
                non-critical product emails (welcome, trial lifecycle,
                roster / leave / RSA / draft notifications). Critical
                emails (password reset, verification, invite, billing)
                still deliver. */}
            <div className="border border-slate-200 rounded-lg p-3 bg-slate-50">
              <label className="flex items-start gap-2 cursor-pointer">
                <input type="checkbox" className="mt-1"
                        checked={!!form.is_test}
                        onChange={(e)=>setForm({...form, is_test: e.target.checked})}
                        data-testid="admin-edit-is-test" />
                <div>
                  <div className="text-sm font-medium text-[#0B1A36]">
                    Mark as test company
                  </div>
                  <div className="text-xs text-[#4B5773] mt-0.5">
                    Skips non-critical emails such as welcome, trial
                    lifecycle, roster published and leave notifications.
                    Password reset, verification, invites and billing
                    emails are unaffected. Super Admin only.
                  </div>
                </div>
              </label>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={onClose} className="rounded-lg border-slate-200">Close</Button>
              <Button type="submit" data-testid="admin-edit-save"
                className="rounded-lg bg-[#2563EB] hover:bg-[#1D4ED8]">Save</Button>
            </DialogFooter>
          </form>
        ) : tab === "billing" ? (
          <div className="mt-4">
            <BillingPanel companyId={company.company_id} onMutated={onSaved}/>
          </div>
        ) : (
          <div className="mt-4">
            <CompanyActivityTab companyId={company.company_id}/>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

/**
 * Phase 2Z.ab - lightweight audit visibility for a single company.
 * Reads the existing /api/audit-logs endpoint (super_admin sees all)
 * filtered by entity_type=company. Append-only history of who did what
 * and when, so the founder can answer "why is this account in this
 * state" months later without a DB query.
 */
function CompanyActivityTab({ companyId }) {
  const [rows, setRows] = useState(null);
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get("/audit-logs", {
          params: { entity_type: "company", entity_id: companyId, limit: 50 },
        });
        if (alive) setRows(data.items || []);
      } catch (e) {
        if (alive) setRows([]);
        toast.error(formatError(e.response?.data?.detail));
      }
    })();
    return () => { alive = false; };
  }, [companyId]);

  if (rows === null) return <div className="text-sm text-[#4B5773] py-6">Loading activity…</div>;
  if (!rows.length) return <div className="text-sm text-[#4B5773] py-6" data-testid="admin-activity-empty">No activity recorded for this company yet.</div>;

  return (
    <div className="space-y-2" data-testid="admin-activity-list">
      <div className="text-xs text-[#4B5773] mb-2">Latest {rows.length} events. Append-only.</div>
      {rows.map((r) => (
        <div key={r.audit_id} data-testid={`admin-activity-row-${r.audit_id}`}
             className="rounded-md border border-slate-200 bg-white p-3 text-xs">
          <div className="flex items-center justify-between gap-2">
            <div className="font-semibold text-[#0B1A36]">{r.action}</div>
            <div className="text-[#4B5773]">{(r.created_at || r.timestamp || "").slice(0, 16).replace("T", " ")}</div>
          </div>
          <div className="text-[#4B5773] mt-1">
            By {r.user_email || r.user_id || "(system)"}
          </div>
          {r.metadata?.reason && (
            <div className="text-[#0B1A36] mt-1">
              <span className="text-[10px] uppercase tracking-widest text-[#4B5773]">Reason</span>{" "}
              {r.metadata.reason}
            </div>
          )}
          {r.metadata?.notes && (
            <div className="text-[#4B5773] mt-1">Notes: {r.metadata.notes}</div>
          )}
          {r.metadata?.fields && Array.isArray(r.metadata.fields) && r.metadata.fields.length > 0 && (
            <div className="text-[#4B5773] mt-1">Fields: {r.metadata.fields.join(", ")}</div>
          )}
        </div>
      ))}
    </div>
  );
}


/**
 * Feb 2026 Super Admin test cleanup tool.
 *
 * Three actions, in order of destructiveness:
 *   1. Reset setup only (safe on production; frontend clears
 *      localStorage keys returned by the backend).
 *   2. Reset test tenant data (preview-only, typed confirm).
 *   3. Delete test tenant (preview-only, typed confirm).
 *
 * All three are Super Admin only (route already gates on super_admin).
 * The backend re-enforces every guardrail regardless of what the
 * frontend shows or hides.
 */
function TestToolsDialog({ company, onClose, onDeleted }) {
  const [action, setAction] = useState("reset_setup"); // or reset_tenant_data / delete_test_tenant
  const [status, setStatus] = useState(null); // safeguard + counts payload
  const [loading, setLoading] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const [resetSetupResult, setResetSetupResult] = useState(null);

  useEffect(() => {
    if (!company) {
      setAction("reset_setup"); setStatus(null); setConfirmText("");
      setResetSetupResult(null); return;
    }
    let alive = true;
    (async () => {
      setLoading(true); setStatus(null); setConfirmText("");
      setResetSetupResult(null);
      try {
        const { data } = await api.get(
          `/admin/companies/${company.company_id}/test-cleanup/status`,
          { params: { action } });
        if (alive) setStatus(data);
      } catch (err) {
        if (alive) toast.error(formatError(err.response?.data?.detail) || "Could not load safeguard status");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [company, action]);

  if (!company) return null;

  const isPreview = !!status?.env?.is_preview;
  const ok = !!status?.ok;
  const counts = status?.counts || {};
  const nonZeroCounts = Object.entries(counts).filter(([, v]) => (v || 0) > 0);
  const requiredPhrase = action === "delete_test_tenant"
    ? "DELETE TEST TENANT"
    : action === "reset_tenant_data" ? "RESET TEST TENANT" : "";
  const canFire = action === "reset_setup"
    ? true
    : (ok && requiredPhrase && confirmText.trim() === requiredPhrase);

  const runResetSetup = async () => {
    setLoading(true);
    try {
      const { data } = await api.post(
        `/admin/companies/${company.company_id}/reset-setup-review`);
      setResetSetupResult(data);
      toast.success("Reset setup instructions ready.");
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail) || "Could not reset setup");
    } finally { setLoading(false); }
  };

  const runReset = async () => {
    setLoading(true);
    try {
      const { data } = await api.post(
        `/admin/companies/${company.company_id}/reset-tenant-data`,
        { confirm: confirmText.trim() });
      toast.success(data.message || "Tenant data reset.");
      setConfirmText(""); onClose();
    } catch (err) {
      const d = err.response?.data?.detail;
      if (d && Array.isArray(d.reasons)) toast.error(`Blocked: ${d.reasons.join(" ")}`);
      else toast.error(formatError(d) || "Could not reset tenant data");
    } finally { setLoading(false); }
  };

  const runDelete = async () => {
    setLoading(true);
    try {
      const { data } = await api.post(
        `/admin/companies/${company.company_id}/delete-test-tenant`,
        { confirm: confirmText.trim() });
      toast.success(data.message || "Test tenant deleted.");
      setConfirmText("");
      if (onDeleted) onDeleted();
      onClose();
    } catch (err) {
      const d = err.response?.data?.detail;
      if (d && Array.isArray(d.reasons)) toast.error(`Blocked: ${d.reasons.join(" ")}`);
      else toast.error(formatError(d) || "Could not delete tenant");
    } finally { setLoading(false); }
  };

  return (
    <Dialog open={!!company} onOpenChange={(o) => !o && onClose()}>
      <DialogContent fullscreenOnMobile
                      className="max-w-2xl max-h-[90vh] overflow-y-auto"
                      data-testid="admin-test-tools-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Wrench className="h-4 w-4 text-[#4B5773]" />
            Test tools
          </DialogTitle>
        </DialogHeader>

        <div className="mt-2 text-xs text-[#4B5773]">
          For <span className="font-semibold text-[#0B1A36]">{company.name}</span>
          {" · "}
          {isPreview ? (
            <span className="text-emerald-700"
                   data-testid="admin-test-tools-preview-badge">Preview environment</span>
          ) : (
            <span className="text-rose-700"
                   data-testid="admin-test-tools-prod-badge">Production. Destructive actions disabled.</span>
          )}
        </div>

        {/* Action picker */}
        <div className="mt-4 flex flex-col gap-2">
          {[
            { key: "reset_setup",
              label: "Reset setup only (safe)",
              hint: "Restart the Account setup journey. Does not delete data. Available on production." },
            { key: "reset_tenant_data",
              label: "Reset test tenant data (preview)",
              hint: "Clear tenant-owned rows and re-seed defaults. Keeps the workspace admin login." },
            { key: "delete_test_tenant",
              label: "Delete one test tenant (preview)",
              hint: "Remove the company and its users so the email can be reused." },
          ].map((a) => (
            <label key={a.key}
                   data-testid={`admin-test-tools-action-${a.key}`}
                   className={`border rounded-lg p-3 cursor-pointer transition-colors ${
                     action === a.key
                       ? "border-[#2563EB] bg-[#F5F8FE]"
                       : "border-slate-200 hover:border-slate-300"}`}>
              <div className="flex items-start gap-2">
                <input type="radio" name="test-tools-action"
                       className="mt-1"
                       checked={action === a.key}
                       onChange={() => setAction(a.key)} />
                <div className="flex-1">
                  <div className="text-sm font-medium text-[#0B1A36]">{a.label}</div>
                  <div className="text-xs text-[#4B5773] mt-0.5">{a.hint}</div>
                </div>
              </div>
            </label>
          ))}
        </div>

        {/* Safeguard verdict */}
        {loading && !status && (
          <div className="mt-4 text-xs text-[#4B5773]">Loading safety checks...</div>
        )}
        {status && (
          <div className="mt-4 border border-slate-200 rounded-lg p-3 bg-white"
               data-testid="admin-test-tools-safeguard">
            <div className="text-[10px] uppercase tracking-widest text-[#4B5773] font-semibold mb-2">
              Safety check
            </div>
            {ok ? (
              <div className="text-xs text-emerald-700"
                   data-testid="admin-test-tools-safeguard-ok">
                Allowed. All guardrails passed for this action.
              </div>
            ) : (
              <div className="text-xs text-rose-700"
                   data-testid="admin-test-tools-safeguard-blocked">
                <div className="flex items-center gap-1 mb-1 font-semibold">
                  <ShieldAlert className="h-3.5 w-3.5" />
                  Blocked
                </div>
                <ul className="list-disc list-inside space-y-0.5">
                  {(status.blocked_reasons || []).map((r, i) => (
                    <li key={i}>{r}</li>
                  ))}
                </ul>
              </div>
            )}
            {status.test_marker_reasons?.length > 0 && (
              <div className="mt-2 text-[11px] text-[#4B5773]">
                Test markers detected: {status.test_marker_reasons.join(" · ")}
              </div>
            )}
          </div>
        )}

        {/* Dry-run counts (skip for reset_setup which is data-free) */}
        {status && action !== "reset_setup" && (
          <div className="mt-3 border border-slate-200 rounded-lg p-3 bg-white"
               data-testid="admin-test-tools-counts">
            <div className="text-[10px] uppercase tracking-widest text-[#4B5773] font-semibold mb-2">
              Dry run counts
            </div>
            {nonZeroCounts.length === 0 ? (
              <div className="text-xs text-[#4B5773]">Nothing to remove.</div>
            ) : (
              <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
                {nonZeroCounts.map(([k, v]) => (
                  <div key={k} className="flex justify-between text-[#0B1A36]">
                    <span className="text-[#4B5773]">{k}</span>
                    <span className="font-medium tabular-nums">{v}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Reset setup only: result panel with browser snippet */}
        {action === "reset_setup" && resetSetupResult && (
          <div className="mt-3 border border-emerald-200 rounded-lg p-3 bg-emerald-50 text-xs text-[#0B1A36]"
               data-testid="admin-test-tools-reset-setup-result">
            <div className="font-semibold">Setup guide is ready to be reset.</div>
            <div className="text-[#4B5773] mt-1">{resetSetupResult.message}</div>
            <div className="mt-2">
              <div className="text-[10px] uppercase tracking-widest text-[#4B5773] font-semibold">
                Run in the browser console
              </div>
              <div className="flex items-center gap-2 mt-1">
                <code className="flex-1 text-[11px] bg-white border border-slate-200 rounded p-2 break-all"
                      data-testid="admin-test-tools-snippet">
                  {resetSetupResult.browser_snippet}
                </code>
                <Button size="sm" variant="outline"
                        onClick={() => {
                          try { navigator.clipboard.writeText(resetSetupResult.browser_snippet); toast.success("Copied"); }
                          catch { /* ignore */ }
                        }}
                        data-testid="admin-test-tools-copy-snippet">
                  <ClipboardCopy className="h-3.5 w-3.5" />
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Typed confirm (only for destructive actions) */}
        {action !== "reset_setup" && ok && (
          <div className="mt-3">
            <Label className="eyebrow">Type {requiredPhrase} to confirm</Label>
            <Input value={confirmText}
                   onChange={(e) => setConfirmText(e.target.value)}
                   placeholder={requiredPhrase}
                   className="mt-1.5 h-10 rounded-lg"
                   data-testid="admin-test-tools-confirm-input" />
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose}
                   className="rounded-lg border-slate-200"
                   data-testid="admin-test-tools-cancel">
            Close
          </Button>
          {action === "reset_setup" && (
            <Button onClick={runResetSetup} disabled={loading}
                     className="rounded-lg bg-[#2563EB] hover:bg-[#1D4ED8]"
                     data-testid="admin-test-tools-run-reset-setup">
              <RotateCcw className="h-4 w-4 mr-1" />
              Reset setup only
            </Button>
          )}
          {action === "reset_tenant_data" && (
            <Button onClick={runReset}
                     disabled={loading || !canFire}
                     className="rounded-lg bg-amber-600 hover:bg-amber-700 text-white"
                     data-testid="admin-test-tools-run-reset-tenant">
              <RotateCcw className="h-4 w-4 mr-1" />
              Reset tenant data
            </Button>
          )}
          {action === "delete_test_tenant" && (
            <Button onClick={runDelete}
                     disabled={loading || !canFire}
                     className="rounded-lg bg-rose-600 hover:bg-rose-700 text-white"
                     data-testid="admin-test-tools-run-delete-tenant">
              <Trash2 className="h-4 w-4 mr-1" />
              Delete test tenant
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

